import React, { useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import classnames from "classnames";
import { Field } from "formik";
import { Row, Col, Button } from "antd";
import i18n from "i18n";
import { Tooltip } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  CaretDownOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { FlattenedPickedQuestion } from "api/sms-api";
import { DEFAULT_VALUE } from "./QuestionEdit";
import {
  CgRadioCheck,
  CgShapeSquare,
  CgMathMinus,
  CgMathEqual,
  CgCloseR,
} from "react-icons/cg";

interface SortableItemProps {
  value: FlattenedPickedQuestion;
  index: number;
  parentIndex: number | undefined;
  grandParentIndex: number | undefined;
  collapsed: boolean;
  multiQuestionIds?: string[];
  allItems?: FlattenedPickedQuestion[]; // 子要素インデックス計算用

  // ヘッダー表示用の情報
  itemLength: number;
  displayCount: number;
  inputCount: number;
  surveyOnlyCount: number;
  rejectedCount: number;

  handleClickDisplayRequired: () => void;
  handleClickInputRequired: () => void;
  handleClickSurveyOnly: () => void;
  deleteSelectedQuestions: () => void;
  handleClickQuestion: () => void;
  handleClickArchive: () => void;
  handleCollapse?(): void;
  duplicateQuestion: () => void;
}

const SortableItem: React.FC<SortableItemProps> = (props) => {
  const {
    setNodeRef,
    listeners,
    attributes,
    transform,
    isDragging,
  } = useSortable({
    id: props.value.selectId || "",
  });
  const classes = classnames("survey-dnd-item", {
    dragging: isDragging,
    surveyOnly: props.value.surveyOnly,
    archive: props.value.archive,
    group: props.value.formType === "group",
  });
  const multiQuestionIds = props.multiQuestionIds ?? [];

  let hidden = props.value.depth > 0;
  if (props.value.formType === "group" && props.value.parentId) {
    hidden = false;
  }
  if (props.value.parentId && multiQuestionIds.includes(props.value.parentId)) {
    hidden = false;
  }

  let title = props.value.title;
  if (props.value.formType === "group") {
    const randomCount = props.value.options?.randomCount;
    const displayRandomCount =
      Number(randomCount) === 0
        ? i18n.t("survey.questionText.selectAll")
        : `${randomCount ?? DEFAULT_VALUE}`;
    title = `${title}（${displayRandomCount}/${props.value.children.length}）`;
  }

  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState("");

  const handleClickDisplayRequired = () => {
    props.handleClickDisplayRequired();
  };
  const handleClickInputRequired = () => {
    props.handleClickInputRequired();
  };
  const handleClickSurveyOnly = () => {
    props.handleClickSurveyOnly();
  };
  const handleClickArchive = () => {
    props.handleClickArchive();
  };
  const handleClickDelete = () => {
    props.deleteSelectedQuestions();
  };
  const handleEditButtonClick = () => {
    props.handleClickQuestion();
  };
  const handleDuplicateButtonClick = () => {
    props.duplicateQuestion();
  };

  const handleMouseEnter = (tooltipName: string) => {
    setTooltipVisible(true);
    setActiveTooltip(tooltipName);
  };

  const handleMouseLeave = () => {
    setTooltipVisible(false);
    setActiveTooltip("");
  };

  // 分岐用選択肢
  const isBranch = props.value.formType === "branch";
  const isBranchOption = props.value.formType === "branch_option";
  // 質問グループ
  const isGroup = props.value.formType === "group";
  // 複数質問
  const isMultiQuestion = props.value.formType === "multi_question";
  // 基本情報
  const isEmail = props.value.formType === "email";
  const isGender = props.value.formType === "gender";
  const isAge = props.value.formType === "age";
  const isName = props.value.formType === "name";
  const isHurigana = props.value.formType === "hurigana";
  const isBirth = props.value.formType === "birth";
  const isPrefectures = props.value.formType === "prefectures";
  const isConditions = props.value.formType === "conditions";
  const isGenerations = props.value.formType === "generations";
  const isPostCode = props.value.formType === "post_code";
  const isPhone = props.value.formType === "phone";
  const isEmailOption = props.value.formType === "email_option";

  // 質問項目
  const isText = props.value.formType === "text";
  const isComment = props.value.formType === "comment";
  const isRating = props.value.formType === "rating";
  const isNumber = props.value.formType === "number";
  const isRadiogroup = props.value.formType === "radiogroup";
  const isCheckbox = props.value.formType === "checkbox";
  const isPromptRadioButton = props.value.formType === "promptradiobutton";
  const isNps = props.value.formType === "nps";

  // 複数設問の子要素判定
  const isChildOfMultiQuestion =
    props.value.parentId && multiQuestionIds.includes(props.value.parentId);

  // 表示必須チェックボックス
  const displayRequiredCheckboxClasses = classnames("survey-dnd-checkbox", {
    hidden: hidden || isChildOfMultiQuestion,
  });

  // 入力必須チェックボックス
  const inputRequiredCheckboxClasses = classnames("survey-dnd-checkbox", {
    hidden: hidden || isMultiQuestion,
  });

  // アンケ専用チェックボックス
  const surveyOnlyCheckboxClasses = classnames("survey-dnd-checkbox", {
    hidden: hidden || isMultiQuestion,
  });

  // 不採用チェックボックス
  const archiveCheckboxClasses = classnames("survey-dnd-checkbox", {
    hidden: false,
  });

  // 子要素インデックスの計算関数
  const getChildIndex = (
    currentItem: FlattenedPickedQuestion,
    allItems?: FlattenedPickedQuestion[]
  ): number => {
    if (!allItems) return 1;

    // 親がない場合はトップレベル要素として自身のindexを優先
    if (!currentItem.parentId) {
      return currentItem.index ?? 1;
    }

    // 同じ親を持つ子要素のみを取得してソート
    const siblings = allItems
      .filter((item) => item.parentId === currentItem.parentId)
      .sort((a, b) => (a.index || 0) - (b.index || 0));

    // 現在のアイテムが何番目かを取得
    const siblingIndex = siblings.findIndex(
      (item) => item.selectId === currentItem.selectId
    );

    return siblingIndex !== -1 ? siblingIndex + 1 : 1;
  };

  // 表示用インデックスを計算する関数
  const getDisplayIndex = (
    currentItem: FlattenedPickedQuestion,
    allItems?: FlattenedPickedQuestion[]
  ): number => {
    if (!allItems) return 1;

    if (currentItem.depth === 0) {
      // 最上位要素の場合、depth=0の要素の中での順序
      const topLevelItems = allItems
        .filter((item) => item.depth === 0)
        .sort((a, b) => (a.index || 0) - (b.index || 0));
      const displayIndex = topLevelItems.findIndex(
        (item) => item.selectId === currentItem.selectId
      );

      return displayIndex !== -1 ? displayIndex + 1 : 1;
    }

    return getChildIndex(currentItem, allItems);
  };

  // 汎用的な階層インデックス計算
  const buildHierarchicalIndex = (
    currentItem: FlattenedPickedQuestion,
    allItems?: FlattenedPickedQuestion[]
  ): string => {
    if (!allItems) return "1";

    const indices: number[] = [];
    let currentNode = currentItem;

    // 現在の要素から最上位まで遡ってインデックスを収集
    while (true) {
      // 現在の階層での順序を計算
      const currentIndex = getChildIndex(currentNode, allItems);
      // 配列の先頭に追加
      indices.unshift(currentIndex);
      // 親要素がない場合（最上位要素）、最上位での順序を計算し直し
      if (!currentNode.parentId) {
        const topLevelIndex = getDisplayIndex(currentNode, allItems);
        indices[0] = topLevelIndex;
        break;
      }
      // 親要素を取得して続行
      const parentItem = allItems.find(
        (item) => item.selectId === currentNode.parentId
      );
      if (!parentItem) break;
      currentNode = parentItem;
    }

    return indices.join(" - ");
  };

  const index = buildHierarchicalIndex(props.value, props.allItems);

  // 分岐選択肢内グループで最初の項目の削除ボタンは非表示にする
  const isDeleteButtonVisible =
    props.value.formType === "group" &&
    typeof props.parentIndex !== "undefined" &&
    props.index === 0;

  return (
    <>
      <div
        style={{
          display: "flex",
          width: "100%",
          margin: "0 auto",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            flex: "1 1 0%",
            minWidth: 0,
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: "auto",
              minWidth: "50px",
              textAlign: "center",
              marginLeft: `${50 * props.value.depth}px`,
              marginRight: "10px",
              flex: "none",
              whiteSpace: "nowrap",
            }}
          >
            {index}
          </div>
          <div style={{ flexGrow: 1, paddingRight: "8px", minWidth: 0 }}>
            <li
              key={props.value.selectId}
              ref={setNodeRef}
              {...attributes}
              style={
                {
                  "--translate-x": transform
                    ? `${Math.round(transform.x)}px`
                    : undefined,
                  "--translate-y": transform
                    ? `${Math.round(transform.y)}px`
                    : undefined,
                  width: "100%",
                  paddingLeft: "18px",
                  paddingRight: "18px",
                } as React.CSSProperties
              }
              className={`${classes} ${
                isBranch || isBranchOption ? "branch" : ""
              }`}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                {isBranch ||
                isBranchOption ||
                isGroup ||
                isMultiQuestion ||
                isRating ||
                isNumber ? null : (
                  <div
                    className="survey-dnd-item-icon survey-question-icon"
                    style={{ width: "30px" }}
                  >
                    {isRadiogroup ||
                    isPromptRadioButton ||
                    isGender ||
                    isGenerations ||
                    isNps ? (
                      <CgRadioCheck />
                    ) : null}
                    {isCheckbox || isConditions ? <CgShapeSquare /> : null}
                    {isText ||
                    isHurigana ||
                    isAge ||
                    isBirth ||
                    isPrefectures ||
                    isPostCode ? (
                      <CgMathMinus />
                    ) : null}
                    {isComment ? <CgMathEqual /> : null}
                    {isPhone || isEmail || isName || isEmailOption ? (
                      <CgCloseR />
                    ) : null}
                  </div>
                )}
                <div
                  {...listeners}
                  style={{
                    flexGrow: 1,
                    marginLeft: "10px",
                    marginRight: "10px",
                  }}
                >
                  {isEmailOption ? (
                    <span style={{ fontWeight: "bold", color: "#ff0000" }}>
                      {title}
                    </span>
                  ) : (
                    title
                  )}
                </div>
                {props.handleCollapse && (
                  <div
                    className="survey-dnd-item-icon"
                    style={{ width: "30px" }}
                  >
                    <Button type="text" onClick={props.handleCollapse}>
                      <CaretDownOutlined
                        rotate={props.collapsed ? 180 : undefined}
                      />
                    </Button>
                  </div>
                )}
                <div className="survey-dnd-item-icon" style={{ width: "30px" }}>
                  <Button type="text" onClick={handleEditButtonClick}>
                    <EditOutlined
                      style={{
                        color: "#1890ff",
                      }}
                    />
                  </Button>
                </div>
                <div className="survey-dnd-item-icon" style={{ width: "30px" }}>
                  <Button type="text" onClick={handleDuplicateButtonClick}>
                    <PlusCircleOutlined />
                  </Button>
                </div>
              </div>
            </li>
          </div>
        </div>
        <Row
          justify="end"
          align="middle"
          style={{
            width: "40%",
            height: "100%",
            margin: "5px",
            paddingLeft: "10px",
            paddingRight: 0,
          }}
        >
          <Col span={4} className={displayRequiredCheckboxClasses}>
            <Tooltip
              title={i18n.t("survey.displayRequired")}
              placement="top"
              overlayStyle={{ textAlign: "center" }}
              visible={tooltipVisible && activeTooltip === "displayRequired"}
            >
              <div
                onMouseEnter={() => handleMouseEnter("displayRequired")}
                onMouseLeave={handleMouseLeave}
                className="survey-dnd-checkbox-tooltip"
              >
                <Field
                  type="checkbox"
                  name="displayRequired"
                  checked={props.value.displayRequired}
                  value={props.value.selectId}
                  onClick={handleClickDisplayRequired}
                />
              </div>
            </Tooltip>
          </Col>
          <Col span={5} className={inputRequiredCheckboxClasses}>
            <Tooltip
              title={i18n.t("survey.inputRequired")}
              placement="top"
              overlayStyle={{ textAlign: "center" }}
              visible={tooltipVisible && activeTooltip === "inputRequired"}
            >
              <div
                onMouseEnter={() => handleMouseEnter("inputRequired")}
                onMouseLeave={handleMouseLeave}
                className="survey-dnd-checkbox-tooltip"
              >
                <Field
                  type="checkbox"
                  name="inputRequired"
                  checked={props.value.inputRequired}
                  value={props.value.selectId}
                  onClick={handleClickInputRequired}
                />
              </div>
            </Tooltip>
          </Col>
          <Col span={5} className={surveyOnlyCheckboxClasses}>
            <Tooltip
              title={i18n.t("survey.surveyOnly")}
              placement="top"
              overlayStyle={{ textAlign: "center" }}
              visible={tooltipVisible && activeTooltip === "surveyOnly"}
            >
              <div
                onMouseEnter={() => handleMouseEnter("surveyOnly")}
                onMouseLeave={handleMouseLeave}
                className="survey-dnd-checkbox-tooltip"
              >
                <Field
                  type="checkbox"
                  name="surveyOnly"
                  checked={props.value.surveyOnly}
                  value={props.value.selectId}
                  onClick={handleClickSurveyOnly}
                />
              </div>
            </Tooltip>
          </Col>
          <Col span={5} className={archiveCheckboxClasses}>
            <Tooltip
              title={i18n.t("survey.archive")}
              placement="top"
              overlayStyle={{ textAlign: "center" }}
              visible={tooltipVisible && activeTooltip === "archive"}
            >
              <div
                onMouseEnter={() => handleMouseEnter("archive")}
                onMouseLeave={handleMouseLeave}
                className="survey-dnd-checkbox-tooltip"
              >
                <Field
                  type="checkbox"
                  name="archive"
                  checked={props.value.archive}
                  value={props.value.selectId}
                  onClick={handleClickArchive}
                />
              </div>
            </Tooltip>
          </Col>
          <Col
            span={5}
            style={{
              height: 40,
              color: "red",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              visibility: isDeleteButtonVisible ? "hidden" : undefined,
              margin: 0,
            }}
          >
            <DeleteOutlined onClick={handleClickDelete} />
          </Col>
        </Row>
      </div>
    </>
  );
};

export default SortableItem;
